<template>
  <div>
    <Modal
      :show="showModal"
      size="md"
      dialog-max-width="600px"
      :zIndex="10060"
      :show-close-button="false"
      :blur-backdrop="false"
      :backdropOpacity="0.3"
      :closeOnBackdrop="false"
      @close="closeModal"
      headerVariant="blue"
    >
      <template #header>
        <div class="book-meeting-header">
          <div class="book-meeting-header__text">
            <h2 class="book-meeting-header__title">
              {{ isReschedule ? "Reschedule Meeting" : "Book A Meeting" }}
            </h2>
            <p class="book-meeting-header__subtitle">
              {{
                isReschedule
                  ? "Change the meeting date and time"
                  : "Schedule a meeting with this contact"
              }}
            </p>
          </div>
          <button
            type="button"
            class="book-meeting-header__close"
            @click="closeModal()"
            aria-label="Close modal"
          >
            <Icon icon="mdi:close" />
          </button>
        </div>
      </template>
      <template #default>
        <form id="book-meeting-form" class="form" @submit.prevent="bookMeeting" novalidate>
          <div class="modal-body-content">
            <div class="form-group">
              <Input
                v-model="bookMeetingObj.title"
                type="text"
                placeholder="Enter meeting title"
                label="Meeting Title"
                :required="true"
                :error="titleError"
                :disabled="isReschedule"
                @input="titleError = null"
              />
            </div>
            <div class="row">
              <div class="col-6">
                <div class="form-group">
                  <DateSelector
                    v-model="bookMeetingObj.date"
                    label="Meeting Date"
                    :required="true"
                    :error="dateError"
                    @update:modelValue="dateError = null"
                  />
                </div>
              </div>
              <div class="col-6">
                <div class="form-group">
                  <TimeSelector
                    v-model="bookMeetingObj.time"
                    :interval="15"
                    :start-hour="8"
                    label="Meeting Time"
                    :required="true"
                    :error="timeError"
                    :busy-slots="busySlots"
                    :loading-slots="isLoadingBusySlots"
                    :timezone="bookMeetingObj.timezone"
                    @update:modelValue="timeError = null"
                  />
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-6">
                <div class="form-group">
                  <Select
                    v-model="bookMeetingObj.duration"
                    :options="durationOptions"
                    placeholder="Select Duration"
                    label="Duration"
                    :required="true"
                    :error="durationError"
                    @update:modelValue="durationError = null"
                  />
                </div>
              </div>
              <div class="col-6">
                <div class="form-group">
                  <TimezoneSelector
                    v-model="bookMeetingObj.timezone"
                    label="Timezone"
                    :required="true"
                    :show-format-toggle="false"
                    format="12h"
                  />
                </div>
              </div>
            </div>
            <div class="form-group position-relative" style="margin-bottom: 16px">
              <div
                v-if="!isReschedule"
                style="width: 100%; display: flex; gap: 8px; align-items: flex-start"
              >
                <div style="flex: 1">
                  <Input
                    v-model="guestEmail"
                    type="text"
                    placeholder="Enter guest email"
                    label="Add Guest"
                    :error="guestEmailError"
                    @input="guestEmailError = null"
                    @keydown.enter.prevent="addGuestEmail"
                  />
                </div>
                <button
                  type="button"
                  class="btn btn-sm add-option-btn-side"
                  style="margin-top: 28px"
                  @click="addGuestEmail"
                >
                  <Icon icon="ic:round-plus" class="add-option-icon" />
                </button>
              </div>
              <label v-else class="ui-select__label">Guests</label>
              <div v-if="bookMeetingObj.guestEmails.length" class="guest-emails-container">
                <template v-bind:key="index" v-for="(email, index) in bookMeetingObj.guestEmails">
                  <span class="badge badge-email">
                    <div class="badge-email__content">
                      <Icon icon="mdi:account-circle-outline" class="badge-email__icon" />
                      <span class="email">{{ email }}</span>
                      <Icon
                        v-if="!isReschedule"
                        icon="mdi:close"
                        class="badge-email__close"
                        @click="removeGuestEmail(email)"
                      />
                    </div>
                  </span>
                </template>
              </div>
              <span v-else-if="isReschedule" class="detail-value--empty-inline">No guests</span>
            </div>
            <div class="row">
              <div class="col-6">
                <div class="row">
                  <div class="col-12">
                    <div class="form-group">
                      <Select
                        v-model="bookMeetingObj.provider"
                        :options="providerOptions"
                        label="Meeting Type"
                        placeholder="Select provider"
                        :clearable="true"
                        :disabled="isReschedule"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-6">
                <div class="form-group">
                  <Input
                    v-model="bookMeetingObj.meetingURL"
                    type="url"
                    :placeholder="
                      isGeneratingLink
                        ? 'Generating link...'
                        : 'Paste meeting link or select provider'
                    "
                    label="Meeting Link"
                    :error="meetingURLError"
                    :disabled="isGeneratingLink || (isReschedule && !!bookMeetingObj.provider)"
                    @input="onMeetingURLManualInput"
                  >
                    <template #trailing>
                      <Icon
                        icon="mdi:content-copy"
                        style="
                          font-size: 1.1rem;
                          color: #2d60ff;
                          cursor: pointer;
                          pointer-events: all;
                        "
                        @click="
                          bookMeetingObj.meetingURL
                            ? copyToClipboard(bookMeetingObj.meetingURL)
                            : null
                        "
                      />
                    </template>
                  </Input>
                  <span v-if="meetingLinkHint" class="meeting-link-hint">
                    {{ meetingLinkHint }}
                  </span>
                </div>
              </div>
            </div>
            <div
              v-if="!isReschedule && bookMeetingObj.provider && !selectedProviderConnected"
              class="provider-warning"
            >
              <Icon icon="mdi:alert-circle-outline" class="provider-warning__icon" />
              <span>
                <strong>{{ selectedProviderName }}</strong> is not connected.
                <router-link to="/settings/integrations" @click="closeModal()">
                  Connect in Settings →
                </router-link>
              </span>
            </div>
            <div class="row">
              <div class="col-6">
                <div class="form-group">
                  <Select
                    v-model="selectedLeadSource"
                    :options="leadSourceOptions"
                    placeholder="Choose lead source"
                    label="Lead Source"
                    :required="true"
                    label-key="name"
                    value-key="id"
                    :return-object="true"
                    :error="leadSourceError"
                    :disabled="isReschedule"
                    @update:model-value="onLeadSourceChange"
                  />
                </div>
              </div>
              <div v-if="!isReschedule" class="col-6">
                <div class="form-group">
                  <Select
                    v-model="selectedCallDisposition"
                    :options="callDispositions"
                    placeholder="Choose call disposition"
                    label="Call Disposition"
                    :required="true"
                    label-key="name"
                    value-key="id"
                    :return-object="true"
                    :disabled="!!meetingDisposition"
                    @update:model-value="onCallDispositionChange"
                  />
                </div>
              </div>
            </div>
            <div v-if="!isReschedule" class="form-group">
              <label for="message" class="ui-select__label">Add Note</label>
              <RichTextEditor
                v-model="bookMeetingObj.note"
                height="150px"
                placeholder="Enter here"
              />
            </div>
          </div>
        </form>
      </template>
      <template #footer>
        <div class="modal-footer-buttons">
          <Button
            @click="closeModal()"
            type="button"
            variant="secondary"
            :disabled="isSubmitting"
            block
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="book-meeting-form"
            variant="primary"
            :loading="isSubmitting"
            :disabled="isSubmitting || (!!guestEmail && !isValidGuestEmail)"
            block
          >
            {{ isReschedule ? "Save" : "Send Meeting Invite" }}
          </Button>
        </div>
      </template>
    </Modal>

    <!-- Child modals outside Modal for proper viewport centering -->
    <NotesModal
      v-if="showNotesModal == true"
      :selectedContact="selectedBucket?.contact"
      @close-notes-modal="showNotesModal = false"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, watchEffect, nextTick } from "vue";
import { useStore } from "vuex";
import { Clipboard } from "v-clipboard";
import DeleteConfirmationModal from "@/components/modal/DeleteConfirmationModal.vue";
import NotesModal from "@/components/note/NotesModal.vue";
import {
  Button,
  DateSelector,
  Input,
  Modal,
  RichTextEditor,
  Select,
  TimeSelector,
  TimezoneSelector,
} from "@/components/ui";
import { Icon } from "@iconify/vue";
import { IntegrationProviders, TIMEZONES, TaskType } from "@/utils/enums";
import {
  useRegisterMeeting,
  useGenerateMeetingLink,
  useRescheduleMeeting,
} from "@/composables/meetings";
import { useRepDayMeetings } from "@/composables/meetings/useRepDayMeetings";
import { useCallDispositions } from "@/composables/calls";
import { useLeadSources } from "@/composables/lead-source/useLeadSources";
import { GET_INTEGRATIONS } from "@/store/action-types";
import { useToast } from "@/composables/ui/useToast";
import { useFormDraft } from "@/composables/shared";

// Props
const props = defineProps({
  selectedBucket: {
    type: Object,
    default: null,
  },
  selectedMeeting: {
    type: Object,
    default: null,
  },
});

// Emits
const emit = defineEmits([
  "closeBookMeetingModal",
  "show-reminder-followup-modal",
  "meeting-rescheduled",
]);

// Reschedule mode: prefilled from an existing meeting, only date/time are editable
const isReschedule = computed(() => !!props.selectedMeeting);

// Store
const store = useStore();
const account = computed(() => store.getters.account.data);
const $toast = useToast();

// State
const showModal = ref(true);
const showNotesModal = ref(false);
const bookMeetingObj = ref({
  title: null,
  date: null,
  time: null,
  meetingDateTime: null,
  duration: null,
  timezone: null,
  guestEmails: [],
  source: null,
  leadSourceId: null,
  callDispositionId: null,
  note: null,
  contactId: null,
  userId: null,
  organizationId: null,
  provider: null,
  meetingURL: null,
  providerMeetingId: null,
});
const guestEmail = ref(null);
const guestEmailError = ref(null);
const meetingURLError = ref(null);
const titleError = ref(null);
const dateError = ref(null);
const timeError = ref(null);
const durationError = ref(null);
const leadSourceError = ref(null);

// Integrations
const integrations = computed(() => store.getters.integrations?.data ?? []);
const isProviderConnected = (provider) =>
  integrations.value.some((i) => i.provider === provider && i.status !== "error");
const selectedProviderConnected = computed(
  () => !bookMeetingObj.value.provider || isProviderConnected(bookMeetingObj.value.provider)
);
const selectedProviderName = computed(() => {
  const map = { google: "Google Meet", teams: "Microsoft Teams", zoom: "Zoom" };
  return map[bookMeetingObj.value.provider] ?? bookMeetingObj.value.provider;
});

const selectedCallDisposition = ref(null);
const selectedLeadSource = ref(null);

// TanStack Query
const organizationId = computed(() => account.value?.organizationId);

const { data: callDispositionsData } = useCallDispositions(
  computed(() => (organizationId.value ? { organizationId: organizationId.value } : null))
);
const { data: leadSourcesData } = useLeadSources();

const { mutateAsync: registerMeeting, isPending: meetingIsLoading } = useRegisterMeeting();
const { mutateAsync: generateMeetingLink, isPending: isGeneratingLink } = useGenerateMeetingLink();
const { mutateAsync: rescheduleMeeting, isPending: rescheduleIsLoading } = useRescheduleMeeting();

const isSubmitting = computed(() => meetingIsLoading.value || rescheduleIsLoading.value);

// Busy slots from rep's day meetings
const { busySlots, isLoading: isLoadingBusySlots } = useRepDayMeetings({
  userId: computed(() => bookMeetingObj.value.userId),
  dateISO: computed(() => bookMeetingObj.value.date),
  organizationId: computed(() => bookMeetingObj.value.organizationId),
  timezone: computed(() => bookMeetingObj.value.timezone),
});

// Computed
const durationOptions = computed(() => [
  { value: 15, label: "15 min" },
  { value: 30, label: "30 min" },
  { value: 45, label: "45 min" },
  { value: 60, label: "1 hour" },
]);

const leadSourceOptions = computed(() => {
  const leadSources = leadSourcesData.value || [];
  return leadSources.map((ls) => ({
    id: ls.id,
    name: ls.name,
    label: ls.name,
    value: ls.id,
  }));
});

const callDispositions = computed(() => callDispositionsData.value || []);
const meetingDisposition = computed(
  () => callDispositions.value.find((d) => d.name?.toLowerCase() === "meeting") || null
);

const providerOptions = computed(() => [
  {
    value: IntegrationProviders.google,
    label: "Meet",
    icon: "logos:google-meet",
  },
  {
    value: IntegrationProviders.teams,
    label: "Teams",
    icon: "logos:microsoft-teams",
  },
  {
    value: IntegrationProviders.zoom,
    label: "Zoom",
    icon: "akar-icons:zoom-fill",
    iconColor: "#2d8cff",
  },
]);

const isValidGuestEmail = computed(() => {
  if (!guestEmail.value) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail.value);
});

// Draft persistence — only for composing a new meeting. Reschedule mode edits an
// existing, already-saved meeting, so there's nothing to draft/restore there; the
// composable is simply never called in that mode (same reusable useFormDraft used
// by NotesModal/EditNoteModal, unmodified).
let loadBookMeetingDraft = () => null;
let clearBookMeetingDraft = () => {};
if (!isReschedule.value) {
  ({ loadDraft: loadBookMeetingDraft, clearDraft: clearBookMeetingDraft } = useFormDraft(
    bookMeetingObj,
    {
      key: `book-meeting:${props.selectedBucket?.contactId ?? "unknown"}`,
      serialize: (v) => ({
        title: v.title,
        date: v.date,
        time: v.time,
        duration: v.duration,
        timezone: v.timezone,
        guestEmails: v.guestEmails,
        note: v.note,
        provider: v.provider,
        meetingURL: v.meetingURL,
        leadSourceId: v.leadSourceId,
      }),
      isEmpty: (v) =>
        !v.title &&
        !v.date &&
        !v.time &&
        !v.duration &&
        !v.note?.trim() &&
        !v.meetingURL &&
        !v.provider &&
        !v.leadSourceId,
    }
  ));
}

// Lead Source's Select binds the full option object, but the draft only stores the id —
// resolve it against the async-loaded options list once it's available (handles both
// "already loaded" and "loads after restore" timing).
const pendingDraftLeadSourceId = ref(null);
// A restored draft can already carry a provider + meetingURL together. Setting both
// fields trips the "auto-generate meeting link" watch below, which would call the
// provider API again and stomp the restored link with a brand-new one. Suppress that
// single reaction while restoreBookMeetingDraft is applying its values.
const suppressAutoGenerateLink = ref(false);
watchEffect(() => {
  if (!pendingDraftLeadSourceId.value) return;
  const match = leadSourceOptions.value.find((o) => o.id === pendingDraftLeadSourceId.value);
  if (match) {
    selectedLeadSource.value = match;
    pendingDraftLeadSourceId.value = null;
  }
});

// Methods
const setValues = () => {
  if (!props.selectedBucket) return;
  bookMeetingObj.value.source = props.selectedBucket.taskType;
  bookMeetingObj.value.contactId = props.selectedBucket.contactId;
  bookMeetingObj.value.userId = account.value?.id;
  bookMeetingObj.value.organizationId = account.value?.organizationId;
};

const setDefaultTimezone = () => {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timezoneExists = TIMEZONES.find((tz) => tz.value === userTimezone);
  if (timezoneExists) {
    bookMeetingObj.value.timezone = userTimezone;
  } else {
    bookMeetingObj.value.timezone = "America/New_York";
  }
};

const setValuesFromMeeting = () => {
  const meeting = props.selectedMeeting;
  if (!meeting) return;

  const dt = new Date(meeting.meetingDateTime);
  bookMeetingObj.value.title = meeting.title ?? null;
  bookMeetingObj.value.date = new Date(
    dt.getFullYear(),
    dt.getMonth(),
    dt.getDate(),
    0,
    0,
    0,
    0
  ).toISOString();
  bookMeetingObj.value.time = `${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`;
  bookMeetingObj.value.duration = meeting.duration ?? null;
  bookMeetingObj.value.guestEmails = [...(meeting.guestEmails ?? [])];
  bookMeetingObj.value.provider = meeting.provider ?? null;
  bookMeetingObj.value.meetingURL = meeting.meetingURL ?? null;
  bookMeetingObj.value.contactId = meeting.contactId ?? meeting.contact?.id ?? null;
  bookMeetingObj.value.userId = meeting.userId ?? account.value?.id;
  bookMeetingObj.value.organizationId = meeting.organizationId ?? account.value?.organizationId;
  bookMeetingObj.value.leadSourceId = meeting.leadSourceId ?? null;

  if (meeting.leadSource) {
    selectedLeadSource.value = {
      id: meeting.leadSource.id,
      name: meeting.leadSource.name,
      label: meeting.leadSource.name,
      value: meeting.leadSource.id,
    };
  }
};

const prefillGuestFromContact = () => {
  const email = props.selectedBucket?.contact?.email;
  const emailRegExp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (email && emailRegExp.test(email)) {
    if (!bookMeetingObj.value.guestEmails.includes(email)) {
      bookMeetingObj.value.guestEmails.push(email);
    }
  }
};

const restoreBookMeetingDraft = () => {
  const draft = loadBookMeetingDraft();
  if (!draft) return;
  if (draft.provider && draft.meetingURL) suppressAutoGenerateLink.value = true;
  if (draft.title) bookMeetingObj.value.title = draft.title;
  if (draft.date) bookMeetingObj.value.date = draft.date;
  if (draft.time) bookMeetingObj.value.time = draft.time;
  if (draft.duration) bookMeetingObj.value.duration = draft.duration;
  if (draft.timezone) bookMeetingObj.value.timezone = draft.timezone;
  if (draft.guestEmails?.length) {
    bookMeetingObj.value.guestEmails = [
      ...new Set([...bookMeetingObj.value.guestEmails, ...draft.guestEmails]),
    ];
  }
  if (draft.note) bookMeetingObj.value.note = draft.note;
  if (draft.provider) bookMeetingObj.value.provider = draft.provider;
  if (draft.meetingURL) bookMeetingObj.value.meetingURL = draft.meetingURL;
  if (draft.leadSourceId) {
    bookMeetingObj.value.leadSourceId = draft.leadSourceId;
    pendingDraftLeadSourceId.value = draft.leadSourceId;
  }
};

const closeModal = () => {
  emit("closeBookMeetingModal");
};

const copyToClipboard = (value) => {
  Clipboard.copy(value);
  $toast?.info("Copied to clipboard");
};

const addGuestEmail = () => {
  const emailRegExp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!guestEmail.value) {
    guestEmailError.value = null;
    return;
  }
  if (!emailRegExp.test(guestEmail.value)) {
    guestEmailError.value = "Please enter a valid email address";
    return;
  }
  guestEmailError.value = null;
  if (!bookMeetingObj.value.guestEmails.includes(guestEmail.value)) {
    bookMeetingObj.value.guestEmails.push(guestEmail.value);
    guestEmail.value = null;
  }
};

const removeGuestEmail = (email) => {
  bookMeetingObj.value.guestEmails = bookMeetingObj.value.guestEmails.filter((e) => e !== email);
};

const onMeetingURLManualInput = () => {
  meetingURLError.value = null;
  // If user manually types/pastes a link, clear provider context
  if (bookMeetingObj.value.meetingURL) {
    bookMeetingObj.value.provider = null;
    bookMeetingObj.value.providerMeetingId = null;
  }
};

const meetingLinkHint = computed(() => {
  if (!bookMeetingObj.value.provider) return null;
  if (!isProviderConnected(bookMeetingObj.value.provider)) return null;
  const missing = [];
  if (!bookMeetingObj.value.title) missing.push("title");
  if (!bookMeetingObj.value.date) missing.push("date");
  if (!bookMeetingObj.value.time) missing.push("time");
  if (missing.length === 0) return null;
  return `Fill in ${missing.join(", ")} to generate the meeting link`;
});

const handleGenerateMeetingLink = async () => {
  if (!bookMeetingObj.value.provider) return;
  if (!isProviderConnected(bookMeetingObj.value.provider)) return;
  if (!bookMeetingObj.value.title || !bookMeetingObj.value.date || !bookMeetingObj.value.time)
    return;

  const combined = new Date(bookMeetingObj.value.date);
  const [hours, minutes] = bookMeetingObj.value.time.split(":").map(Number);
  combined.setHours(hours, minutes, 0, 0);
  bookMeetingObj.value.meetingDateTime = combined;

  try {
    const res = await generateMeetingLink(bookMeetingObj.value);
    if (res) {
      bookMeetingObj.value.meetingURL = res.joinUrl;
      bookMeetingObj.value.providerMeetingId = res.meetingId;
    }
  } catch (error) {
    console.error("Error generating meeting link:", error);
  }
};

const handleReschedule = async () => {
  dateError.value = null;
  timeError.value = null;
  durationError.value = null;

  if (!bookMeetingObj.value.date) {
    dateError.value = "Meeting date is required";
    return;
  }
  if (!bookMeetingObj.value.time) {
    timeError.value = "Meeting time is required";
    return;
  }
  if (!bookMeetingObj.value.duration) {
    durationError.value = "Duration is required";
    return;
  }

  const combined = new Date(bookMeetingObj.value.date);
  const [hours, minutes] = bookMeetingObj.value.time.split(":").map(Number);
  combined.setHours(hours, minutes, 0, 0);

  try {
    const payload = {
      id: props.selectedMeeting.id,
      meetingDateTime: combined,
      duration: bookMeetingObj.value.duration,
    };
    // Meeting link is only user-editable (and only sent) when there's no provider —
    // for Google/Teams/Zoom the real link lives on the calendar/video event itself.
    if (!bookMeetingObj.value.provider && bookMeetingObj.value.meetingURL) {
      payload.meetingURL = bookMeetingObj.value.meetingURL;
    }

    const result = await rescheduleMeeting(payload);
    $toast.success("Meeting rescheduled successfully");
    emit("meeting-rescheduled", result ?? { ...payload });
    closeModal();
  } catch (error) {
    console.error("Error rescheduling meeting:", error);
    $toast?.error(error?.message || "Failed to reschedule meeting. Please try again.");
  }
};

const bookMeeting = async () => {
  if (isReschedule.value) {
    return handleReschedule();
  }

  // Reset all errors first
  titleError.value = null;
  dateError.value = null;
  timeError.value = null;
  durationError.value = null;
  leadSourceError.value = null;

  let hasError = false;

  // Validation with error messages
  if (!bookMeetingObj.value.title) {
    titleError.value = "Meeting title is required";
    hasError = true;
  }
  if (!bookMeetingObj.value.date) {
    dateError.value = "Meeting date is required";
    hasError = true;
  }
  if (!bookMeetingObj.value.time) {
    timeError.value = "Meeting time is required";
    hasError = true;
  }
  if (!bookMeetingObj.value.duration) {
    durationError.value = "Duration is required";
    hasError = true;
  }
  if (!selectedLeadSource.value) {
    leadSourceError.value = "Lead source is required";
    hasError = true;
  }

  if (hasError) return;

  // Silent system checks (no user-facing errors)
  if (!bookMeetingObj.value.contactId) return;
  if (!selectedCallDisposition.value) return;

  // Require either provider or custom meeting link
  if (!bookMeetingObj.value.provider && !bookMeetingObj.value.meetingURL) {
    meetingURLError.value = "Please select a meeting type or provide a custom meeting link";
    return;
  }

  // If provider is selected, check it's connected
  if (bookMeetingObj.value.provider && !selectedProviderConnected.value) return;

  // Validate custom URL format when no provider
  if (!bookMeetingObj.value.provider && bookMeetingObj.value.meetingURL) {
    try {
      const url = new URL(bookMeetingObj.value.meetingURL);
      if (!["http:", "https:"].includes(url.protocol)) {
        meetingURLError.value = "Please enter a valid URL (e.g., https://meet.example.com/abc)";
        return;
      }
    } catch {
      meetingURLError.value = "Please enter a valid URL (e.g., https://meet.example.com/abc)";
      return;
    }
  }
  meetingURLError.value = null;

  // Combine date and time into meetingDateTime
  const combined = new Date(bookMeetingObj.value.date);
  const [hours, minutes] = bookMeetingObj.value.time.split(":").map(Number);
  combined.setHours(hours, minutes, 0, 0);
  bookMeetingObj.value.meetingDateTime = combined;

  bookMeetingObj.value.leadSourceId = selectedLeadSource.value?.id || null;
  bookMeetingObj.value.callDispositionId = selectedCallDisposition.value?.id || null;

  try {
    const res = await registerMeeting(bookMeetingObj.value);
    if (res) {
      clearBookMeetingDraft();
      emit("show-reminder-followup-modal", res);
      closeModal();
    }
  } catch (error) {
    console.error("Error booking meeting:", error);
  }
};

const onCallDispositionChange = (disposition) => {
  selectedCallDisposition.value = disposition;
  bookMeetingObj.value.callDispositionId = disposition?.id || null;
};

const onLeadSourceChange = (leadSource) => {
  leadSourceError.value = null;
  selectedLeadSource.value = leadSource;
  bookMeetingObj.value.leadSourceId = leadSource?.id || null;
};

const applyDefaultTime = () => {
  // Don't overwrite if user already set a time
  if (bookMeetingObj.value.time) return;
  // Don't set if no date is selected yet
  if (!bookMeetingObj.value.date) return;

  const selectedDate = new Date(bookMeetingObj.value.date);
  const today = new Date();

  const isToday =
    selectedDate.getFullYear() === today.getFullYear() &&
    selectedDate.getMonth() === today.getMonth() &&
    selectedDate.getDate() === today.getDate();

  if (isToday) {
    // Round up to next 30-min slot
    const mins = today.getHours() * 60 + today.getMinutes();
    const next = Math.ceil(mins / 30) * 30;

    if (next <= 17 * 60 + 30) {
      // Format as "HH:MM"
      const hours = String(Math.floor(next / 60)).padStart(2, "0");
      const minutes = String(next % 60).padStart(2, "0");
      bookMeetingObj.value.time = `${hours}:${minutes}`;
    } else {
      // After business hours → default to 09:00
      bookMeetingObj.value.time = "09:00";
    }
  } else {
    // Future date → default to 09:00
    bookMeetingObj.value.time = "09:00";
  }
};

// Auto-select "Meeting" disposition when data loads
watch(
  meetingDisposition,
  (disposition) => {
    if (disposition) {
      selectedCallDisposition.value = disposition;
      bookMeetingObj.value.callDispositionId = disposition.id;
    }
  },
  { immediate: true }
);

// Watch provider changes to generate meeting link
watch(
  () => [
    bookMeetingObj.value.provider,
    bookMeetingObj.value.title,
    bookMeetingObj.value.date,
    bookMeetingObj.value.time,
  ],
  () => {
    // Never regenerate/create a new provider event while rescheduling — the existing
    // link must be preserved; the backend patches the calendar event's time directly.
    if (isReschedule.value) return;
    if (suppressAutoGenerateLink.value) return;
    if (
      bookMeetingObj.value.provider &&
      bookMeetingObj.value.title &&
      bookMeetingObj.value.date &&
      bookMeetingObj.value.time
    ) {
      handleGenerateMeetingLink();
    }
  }
);

// When provider is cleared, also clear auto-generated meeting link
watch(
  () => bookMeetingObj.value.provider,
  (newVal, oldVal) => {
    if (!newVal && oldVal) {
      bookMeetingObj.value.meetingURL = null;
      bookMeetingObj.value.providerMeetingId = null;
      meetingURLError.value = null;
    }
  }
);

// Apply default time when date changes
watch(
  () => bookMeetingObj.value.date,
  () => {
    applyDefaultTime();
  }
);

// Lifecycle
onMounted(async () => {
  if (isReschedule.value) {
    setValuesFromMeeting();
    setDefaultTimezone();
    return;
  }
  setValues();
  setDefaultTimezone();
  prefillGuestFromContact();
  restoreBookMeetingDraft();
  applyDefaultTime();
  // Let the pending watchers triggered by the restore above flush (and see the
  // suppression flag) before clearing it.
  await nextTick();
  suppressAutoGenerateLink.value = false;
  store.dispatch(GET_INTEGRATIONS, {
    userId: account.value?.id,
    organizationId: account.value?.organizationId,
  });
});
</script>

<style scoped>
.book-meeting-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.book-meeting-header__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.book-meeting-header__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  line-height: 1.3;
}

.book-meeting-header__subtitle {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.3;
}

.book-meeting-header__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  cursor: pointer;
  transition: background 0.2s;
  flex-shrink: 0;
}

.book-meeting-header__close:hover {
  background: rgba(255, 255, 255, 0.25);
}

.book-meeting-header__close .iconify {
  width: 16px;
  height: 16px;
}

.modal-body-content {
  padding: 15px 20px;
  max-height: 65vh;
  overflow-y: auto;
}

.guest-emails-container {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.badge-email {
  padding: 4px 8px 4px 6px;
  border-radius: 50px;
  background-color: #e9e9e9;
  display: inline-flex;
}

.badge-email__content {
  display: flex;
  align-items: center;
  gap: 6px;
}

.badge-email__icon {
  font-size: 15px;
  color: #6b7280;
  flex-shrink: 0;
}

.badge-email .email {
  color: #4d5e80;
  font-weight: 400;
  font-size: 0.75rem;
}

.badge-email__close {
  font-size: 14px;
  color: #6b7280;
  cursor: pointer;
  flex-shrink: 0;
  transition: color 0.2s ease;
}

.badge-email__close:hover {
  color: #dc3545;
}

.modal-footer-buttons {
  display: flex;
  gap: 12px;
  width: 100%;
}

.modal-footer-buttons .ui-button {
  flex: 1;
  min-width: 0;
}

.add-option-btn-side {
  background-color: #2d60ff;
  border: none;
  border-radius: 6px;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
  align-self: center;
  margin-left: -4px;
  transition:
    background-color 0.2s ease,
    transform 0.15s ease;
}

.add-option-btn-side:hover {
  background-color: #1a4fe6;
  transform: scale(1.15);
}

.add-option-icon {
  font-size: 15px;
  color: #ffffff;
}

.ui-select__label {
  display: block;
  font-size: 12px;
  font-weight: 400;
  color: #344054;
  margin-bottom: 6px;
}

.row {
  margin-left: -10px;
  margin-right: -10px;
}

.row > [class*="col-"] {
  padding-left: 10px;
  padding-right: 10px;
}

.meeting-link-hint {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  font-weight: 500;
  color: var(--color-warning);
}

.provider-warning {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  font-size: 0.8125rem;
  color: #92400e;
  margin-bottom: 12px;
}

.provider-warning__icon {
  font-size: 1rem;
  color: #d97706;
  flex-shrink: 0;
}

.provider-warning a {
  color: #2d60ff;
  font-weight: 500;
  text-decoration: none;
  margin-left: 4px;
}

.provider-warning a:hover {
  text-decoration: underline;
}

.detail-value--empty-inline {
  display: inline-block;
  margin-top: 8px;
  font-size: 12px;
  color: #9ca3af;
}
</style>
